"use client";
import Link from 'next/link';
import Image from 'next/image';

import { CheckCircle2, Globe2, Ship, FileText, BadgeCheck, Phone, PackageSearch, Factory, Anchor, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { siteConfig } from '@/data/siteConfig'

export function ExportPage() {
  return (
    <>
      {/* ExportPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://jamnagarmetals.com/export',
          name: 'Export Desk | Jamnagar Metals',
          url: 'https://jamnagarmetals.com/export',
          description: 'Global exporter of precision industrial hardware. Serving buyers across 30+ countries with competitive pricing.',
          publisher: { '@id': 'https://jamnagarmetals.com/#organization' },
        })
      }} />

      {/* Hero */}
      <div className="relative py-20 px-4 overflow-hidden" style={{ backgroundColor: '#1C1E24' }}>
        <Image src="/hero.webp" alt="Export Desk Hero" fill priority className="object-cover opacity-15" quality={75} />
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: '#C79A5C' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <Badge className="mb-4 text-xs text-white border-white/30" style={{ backgroundColor: 'rgba(46,109,164,0.5)' }}>
            Worldwide Export · From {siteConfig.export.portOfLoading}
          </Badge>
          <h1 className="text-4xl font-extrabold text-white mb-4 tracking-tight">
            {siteConfig.export.headline}
          </h1>
          <p className="text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Delivering high-quality architectural hardware to over 30 countries globally with dedicated B2B export support.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">

        {/* Key Export Info */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-8">
            <div>
              <h2 className="text-3xl font-bold tracking-tight mb-4">Global Trading Terms</h2>
              <p className="text-muted-foreground leading-relaxed text-lg max-w-2xl">
                We understand the complexities of international trade and provide flexible, secure, and clear terms to ensure smooth transactions for our global partners.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-muted/30 p-6 rounded-2xl border border-border hover:border-[#C79A5C]/50 transition-colors">
                <FileText className="h-8 w-8 text-[#C79A5C] mb-4" />
                <h3 className="font-semibold mb-2">Supported Incoterms</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{siteConfig.export.incoterms.join(', ')}</p>
              </div>
              <div className="bg-muted/30 p-6 rounded-2xl border border-border hover:border-[#C79A5C]/50 transition-colors">
                <Globe2 className="h-8 w-8 text-[#C79A5C] mb-4" />
                <h3 className="font-semibold mb-2">Accepted Currencies</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{siteConfig.export.currencies.join(', ')}</p>
              </div>
              <div className="sm:col-span-2 bg-muted/30 p-6 rounded-2xl border border-border hover:border-[#C79A5C]/50 transition-colors flex items-center gap-4">
                <Ship className="h-10 w-10 text-[#C79A5C] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Port of Loading</h3>
                  <p className="text-sm text-muted-foreground">{siteConfig.export.portOfLoading}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#C79A5C]/10 to-transparent rounded-3xl blur-2xl" />
            <div className="relative bg-background/80 backdrop-blur-xl p-8 rounded-3xl border border-border shadow-2xl space-y-6 h-full flex flex-col justify-center">
              <h3 className="text-2xl font-bold border-b border-border pb-4">Payment & Logistics</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold mb-2">Payment Terms</p>
                  <ul className="space-y-2">
                    {siteConfig.export.paymentTerms.map((term, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-[#C79A5C]" /> {term}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Lead Time</p>
                    <p className="font-medium text-sm">{siteConfig.export.leadTime}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sampling</p>
                    <p className="font-medium text-sm">{siteConfig.export.sampling}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-border">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">IEC Code</p>
                  <p className="font-medium font-mono text-lg">{siteConfig.export.iecCode}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Export Process */}
        <section className="relative rounded-3xl bg-[#1C1E24] overflow-hidden p-8 md:p-14 my-12 shadow-xl border border-border/50">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C79A5C] rounded-full blur-[120px] opacity-20" />
          <div className="relative z-10 mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white mb-4">Seamless Export Process</h2>
            <p className="text-white/70 max-w-2xl mx-auto">From initial inquiry to final delivery, we ensure a transparent and efficient process for our international partners.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {[
              { title: '1. Requirement', desc: 'Share your specifications and MOQ requirements.', icon: FileText },
              { title: '2. Sampling', desc: 'We provide pre-production samples for your approval.', icon: PackageSearch },
              { title: '3. Production', desc: 'Precision manufacturing with strict quality control.', icon: Factory },
              { title: '4. Dispatch', desc: 'Secure packaging and on-time port delivery.', icon: Anchor },
            ].map((step, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-colors group">
                <div className="bg-[#C79A5C]/20 p-4 rounded-xl inline-block mb-4 group-hover:scale-110 transition-transform">
                  <step.icon className="h-6 w-6 text-[#C79A5C]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Markets Served & Quality */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="text-2xl font-bold mb-6 tracking-tight">Global Markets Served</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {siteConfig.export.markets.map((market, idx) => (
                <div key={idx} className="bg-muted/20 p-5 rounded-2xl border border-border hover:border-[#C79A5C]/30 transition-colors h-full">
                  <h3 className="text-lg font-bold text-[#C79A5C] mb-3">{market.region}</h3>
                  <ul className="space-y-2">
                    {market.countries.map((country, cIdx) => (
                      <li key={cIdx} className="text-sm text-foreground/80 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C79A5C]/70" />
                        {country}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          
          <div className="lg:col-span-5 flex">
            <div className="bg-[#1C1E24] rounded-3xl p-8 text-white relative overflow-hidden flex flex-col justify-center w-full shadow-lg">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#C79A5C] rounded-full blur-[60px] opacity-30" />
              <BadgeCheck className="h-12 w-12 text-[#C79A5C] mb-6" />
              <h2 className="text-2xl font-bold mb-4 tracking-tight">Quality Assurance</h2>
              <p className="text-white/70 mb-8 leading-relaxed text-sm">
                Our products undergo rigorous testing to meet international standards. We maintain strict quality control throughout the manufacturing process to ensure consistent excellence.
              </p>
              <div className="flex flex-wrap gap-2">
                {siteConfig.export.certifications.map((cert, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border-none text-xs px-3 py-1.5">
                    <CheckCircle2 className="h-3 w-3 mr-1.5 text-[#C79A5C]" />
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden rounded-3xl bg-[#1C1E24] text-white mt-10 border border-border/50">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C79A5C] rounded-full mix-blend-multiply filter blur-[120px] opacity-20" />
          <div className="absolute inset-0 bg-[url('/hero.webp')] bg-cover bg-center opacity-10 mix-blend-overlay" />
          
          <div className="relative px-6 py-16 md:py-20 flex flex-col items-center text-center">
            <h2 className="text-4xl font-extrabold mb-4 tracking-tight">Discuss Your Export Requirements</h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl">
              Get in touch with our dedicated export desk for competitive container-load pricing, product catalogues, and custom manufacturing inquiries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link href="/quote" className="w-full sm:w-auto">
                <Button size="lg" className="w-full text-white font-bold text-md px-8 py-6 rounded-xl hover:scale-105 transition-transform" style={{ backgroundColor: '#C79A5C' }}>
                  Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link href="/contact" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white text-md px-8 py-6 rounded-xl transition-colors">
                  Contact Export Desk
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
